import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

// ─── KarmaCoin ────────────────────────────────────────────────────────────────

export interface KarmaCoinProps {
  /** Size of the circular coin container in px. Defaults to 44. */
  size?: number;
  /** Run the pop-in + jitter entrance animation. Defaults to true. */
  animated?: boolean;
  className?: string;
}

/**
 * Reusable gold coin icon used across karma surfaces.
 * Renders a ✦ glyph inside a radial-gradient circle with an amber glow.
 * When `animated` is true, the coin pops in and jitters on mount.
 */
export function KarmaCoin({ size = 44, animated = true, className }: KarmaCoinProps) {
  const fontSize = Math.round(size * 0.45);

  return (
    <>
      <div
        className={cn('relative shrink-0 flex items-center justify-center rounded-full', className)}
        style={{
          width: size,
          height: size,
          fontSize,
          background:
            'radial-gradient(circle at 35% 35%, rgba(253,230,138,0.22), rgba(234,179,8,0.06))',
          border: '1px solid rgba(251,191,36,0.28)',
          boxShadow: '0 0 20px rgba(251,191,36,0.16)',
          animation: animated ? 'kc-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.05s both' : undefined,
        }}
        aria-hidden="true"
      >
        <span
          style={{
            animation: animated ? 'kc-jitter 0.45s ease-in-out 0.75s 2' : undefined,
            lineHeight: 1,
          }}
        >
          ✦
        </span>
      </div>

      {animated && (
        <style>{`
          @keyframes kc-pop {
            0%   { transform: scale(0) rotate(-20deg); opacity: 0; }
            65%  { transform: scale(1.18) rotate(4deg); opacity: 1; }
            100% { transform: scale(1) rotate(0deg); opacity: 1; }
          }
          @keyframes kc-jitter {
            0%,100% { transform: rotate(0deg) scale(1); }
            25%     { transform: rotate(-14deg) scale(1.15); }
            75%     { transform: rotate(11deg) scale(1.1); }
          }
        `}</style>
      )}
    </>
  );
}

// ─── KarmaCountUp ─────────────────────────────────────────────────────────────

export interface KarmaCountUpProps {
  /** The target value to count up to. Re-triggers animation when changed. */
  value: number;
  /** Animation duration in ms. Defaults to 900. */
  duration?: number;
}

/**
 * Animates a number from 0 (or its previous value) up to `value`
 * using an ease-out cubic curve. Drop-in replacement for a plain number display.
 */
export function KarmaCountUp({ value, duration = 900 }: KarmaCountUpProps) {
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    let start: number | null = null;

    function step(ts: number) {
      if (start === null) start = ts;
      const t = Math.min((ts - start) / duration, 1);
      setDisplay(Math.round((1 - Math.pow(1 - t, 3)) * value));
      if (t < 1) requestAnimationFrame(step);
    }

    const id = requestAnimationFrame(step);
    return () => cancelAnimationFrame(id);
  }, [value, duration]);

  return <>{display}</>;
}
